import { LoadingOutlined, SearchOutlined } from "@ant-design/icons"
import { Divider, Flex, Select, type SelectProps, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { getLeads } from "../../containers/Leads/api/services.ts"
import type { LeadListItem } from "../../containers/Leads/types/types.ts"
import { useDebouncedCallback } from "../../hooks/use-debounced-callback.ts"
import { showErrorNotifications } from "../../utils/handlers/handle-error"

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 400

type LeadOption = BaseOptionType & {
  value: string
  label: string
  data?: LeadListItem
}

export const leadPickerLabel = (
  lead?: Pick<LeadListItem, "uid" | "lead_name"> | null
) => [lead?.uid, lead?.lead_name].filter(Boolean).join(" — ")

export interface LeadPickerSelectProps
  extends Omit<SelectProps, "options" | "loading" | "mode" | "onSearch"> {
  initialOptions?: BaseOptionType[]
}

export function LeadPickerSelect({
  initialOptions = [],
  ...props
}: LeadPickerSelectProps) {
  const { t } = useTranslation()

  const [options, setOptions] = useState<BaseOptionType[]>([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  // Two searches can be in flight across the debounce boundary, and the slower
  // one must not repaint the newer term's rows.
  const latestRequest = useRef(0)

  const load = useCallback(
    async (q: string, currentPage: number, isLoadMore: boolean) => {
      const request = ++latestRequest.current
      try {
        setLoading(true)
        const response = await getLeads({
          q,
          page: currentPage,
          page_size: PAGE_SIZE,
          // A destination/related-record picker has to reach every lead the
          // user can see, not just the Active table's default population —
          // the same widening the Closed-Lost drill already relies on.
          view: "all"
        })
        if (request !== latestRequest.current) return
        // apisauce resolves a refused request rather than throwing it, so a
        // failed search reaches here looking like an empty one.
        if (!response?.ok || !response.data) throw response?.data

        const { results = [], count = 0, next } = response.data
        setTotal(count)
        setHasMore(Boolean(next))

        const loaded: LeadOption[] = results.map((lead) => ({
          value: lead.id,
          label: leadPickerLabel(lead),
          data: lead
        }))
        setOptions((previous) =>
          isLoadMore ? [...previous, ...loaded] : loaded
        )
      } catch (error) {
        if (request !== latestRequest.current) return
        showErrorNotifications(error)
        setHasMore(false)
        if (!isLoadMore) {
          setOptions([])
          setTotal(0)
        }
      } finally {
        if (request === latestRequest.current) setLoading(false)
      }
    },
    []
  )

  const onSearch = useDebouncedCallback((term: string) => {
    setSearch(term)
    setPage(1)
    void load(term, 1, false)
  }, SEARCH_DEBOUNCE_MS)

  const onPopupScroll: SelectProps["onPopupScroll"] = (event) => {
    const element = event.target as HTMLElement
    const atBottom =
      element.scrollTop + element.offsetHeight >= element.scrollHeight - 5

    if (atBottom && hasMore && !loading) {
      const nextPage = page + 1
      setPage(nextPage)
      void load(search, nextPage, true)
    }
  }

  const offered = useMemo(() => {
    // The seed is the caller's already-picked lead, which a term's answer
    // need not contain.
    if (search) return options
    const loaded = new Set(options.map((option) => option.value))
    return [
      ...initialOptions.filter((option) => !loaded.has(option.value)),
      ...options
    ]
  }, [initialOptions, options, search])

  const optionRender: SelectProps["optionRender"] = (option) => {
    const lead = (option.data as LeadOption).data
    const secondary = [lead?.customer_name, lead?.status]
      .filter(Boolean)
      .join(" · ")

    return (
      <Flex vertical>
        <Typography.Text>
          {lead ? leadPickerLabel(lead) : option.label}
        </Typography.Text>
        {!!secondary && (
          <Typography.Text type="secondary" className="text-xs">
            {secondary}
          </Typography.Text>
        )}
      </Flex>
    )
  }

  return (
    <Select
      showSearch
      allowClear
      filterOption={false}
      className="w-full"
      loading={loading}
      options={offered}
      optionRender={optionRender}
      onSearch={onSearch}
      onPopupScroll={onPopupScroll}
      onOpenChange={(open) => {
        // Closing resets the term too: the seed below is merged back only
        // while none is set, and it is what draws the closed control's label.
        setSearch("")
        setPage(1)
        if (open) void load("", 1, false)
      }}
      // Portalled to body it would stay put when the drawer body scrolls.
      getPopupContainer={(node) => node.parentElement ?? document.body}
      placeholder={t("shared.leadPicker.placeholder")}
      suffixIcon={loading ? <LoadingOutlined /> : <SearchOutlined />}
      notFoundContent={
        loading
          ? t("shared.leadPicker.searching")
          : t("shared.leadPicker.notFound")
      }
      popupRender={(menu) => (
        <>
          {menu}

          {!!offered.length && (
            <>
              <Divider style={{ margin: "8px 0" }} />

              <Flex className="p-2" gap="small" justify="center">
                {loading ? (
                  <>
                    <Typography.Text>
                      {t("shared.leadPicker.loading")}
                    </Typography.Text>
                    <LoadingOutlined />
                  </>
                ) : (
                  <Typography.Text type="secondary">
                    {t("shared.leadPicker.found", { count: total })}
                    {hasMore && t("shared.leadPicker.scrollForMore")}
                  </Typography.Text>
                )}
              </Flex>
            </>
          )}
        </>
      )}
      {...props}
    />
  )
}
